import { Box, Button, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSortState, translate, Translate } from 'react-jhipster';
import { RouteComponentProps, Link } from 'react-router-dom';
import { getEntitiesByNameSearch, reset } from './tire-tread-manufacturer.reducer';

export const TireTreadManufacturer = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const didMount = useRef(false);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(
      getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'),
      props.location.search
    )
  );

  const tireTreadManufacturerList = useAppSelector(state => state.tireTreadManufacturer.entities);
  const loading = useAppSelector(state => state.tireTreadManufacturer.loading);
  const totalItems = useAppSelector(state => state.tireTreadManufacturer.totalItems);
  const [nameSearch, setNameSearch] = useState<string>('');
  const updateSuccess = useAppSelector(state => state.tireTreadManufacturer.updateSuccess);

  const isAdmin = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN])
  );

  const headers: IHeaderItem[] = [
    { key: 'name', sortable: true, component: <Translate contentKey="fleetSenseApp.tireTreadManufacturer.name" /> },
    { key: 'type', sortable: false, component: <Translate contentKey="fleetSenseApp.tireTreadManufacturer.type" /> },
  ];

  const data: IListData[] = [
    { type: 'text', value: 'name' },
    { type: 'text', value: 'type' },
    { type: 'editButton', value: 'id', showIfCompanyIsOwner: true },
    { type: 'deleteButton', value: 'id', showIfCompanyIsOwner: true },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(reset());

    dispatch(
      getEntitiesByNameSearch({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        nameSearch,
      })
    );
  }, [paginationState]);

  const sortEntities = () => {
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;

    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (event, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (updateSuccess) {
      getAllEntities();
    }
  }, [updateSuccess]);

  const searchTypes: ISearchTypes[] = [
    {
      value: nameSearch,
      setValue: setNameSearch,
      placeholder: translate('fleetSenseApp.tireTreadManufacturer.home.search'),
      label: translate('fleetSenseApp.tireTreadManufacturer.home.search'),
    },
  ];

  const { match } = props;

  const pageHeaderButtonSx = {
    borderRadius: '8px',
    height: '42px',
    marginBottom: { xs: '0.35em', sm: '0' },
    '&:hover': {
      color: theme.palette.mode === 'dark' ? 'inherit' : 'secondary',
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: 'primary.main',
        borderRadius: '8px',
        paddingTop: '16px',
        textAlign: 'center',
        padding: '24px',
      }}
    >
      <PageHeader
        headingId="tire-tread-manufacturer-heading"
        headingDataCy="TireTreadManufacturerHeading"
        contentKey="fleetSenseApp.tireTreadManufacturer.home.title"
        match={match}
        showButton={false}
        actions={
          <>
            <Button
              component={Link}
              color="secondary"
              variant="contained"
              to="/tire-tread-model"
              startIcon={<ArrowRightAltIcon />}
            >
              <Translate contentKey="fleetSenseApp.tireTreadModel.home.title" />
            </Button>

            <Button
              component={Link}
              color="secondary"
              variant="contained"
              to={`${match.url}/new`}
              id="jh-create-entity"
              data-cy="entityCreateButton"
              startIcon={<AddIcon />}
            >
              <Translate contentKey="fleetSenseApp.tireTreadManufacturer.home.createLabel" />
            </Button>
          </>
        }
      />

      <PageFilters
        setActivePage={setActivePage}
        searchTypes={searchTypes}
        isCompany={false}
        showFilters={['filters']}
      />

      {!loading && (
        <TableComponent
          listData={data}
          list={tireTreadManufacturerList}
          headers={headers}
          match={match}
          sortFunction={sort}
          pagination={paginationState}
        />
      )}

      {!loading && !tireTreadManufacturerList.length && <NoData title="Nenhum dado encontrado" />}

      {loading && <Loading />}

      <PaginationComponent
        paginationState={paginationState}
        totalItems={totalItems}
        onChange={handlePagination}
        list={tireTreadManufacturerList}
      />
    </Box>
  );
};

export default TireTreadManufacturer;